import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Build } from "@mui/icons-material";

import { selectCurrentUser } from "../../redux/slices/authSlice";

// How often to poll for a deploy in progress. Short enough that a
// non-admin user lands on the maintenance page within a few seconds of a
// deploy starting; long enough not to hammer CloudFront/S3 while idle.
const POLL_INTERVAL_MS = 8000;

// Deploy status is a small JSON object written directly to the S3 bucket
// root (NOT part of the versioned build/ output) by client/buildspec.yml —
// {"maintenance": true} the instant a new build starts overwriting live
// files, {"maintenance": false} only once the new build has finished
// syncing AND the CloudFront cache for it has been invalidated. Fetched as
// a plain relative path so it resolves against whatever origin is
// currently serving the app (works the same in dev — where it 404s and is
// treated as "not in maintenance" — and in production).
const STATUS_URL = "/deploy-status.json";

// Wraps the whole app. While a deploy is in progress (maintenance: true):
//   - Anyone NOT logged in as admin gets a hard reload the moment
//     maintenance starts, landing them on the full-screen "we'll be right
//     back" page — even mid-interaction, they never keep working against a
//     site that's about to go half-updated underneath them. Safe to reload
//     into: the S3 sync hasn't started yet when this flips true (buildspec
//     sets it in pre_build, before the build/sync even runs), so the OLD
//     bundle is still fully intact at this point.
//   - An admin instead keeps using the app normally (to verify the deploy
//     once it's done) with a small banner noting one is in progress — no
//     forced reload, so it doesn't interrupt whatever they're doing.
// The poller keeps running underneath either way. The moment maintenance
// flips back to false, EVERYONE gets a hard reload — the JS currently
// running in the browser is whatever was live BEFORE this deploy, so a
// reload is the only way to actually pick up the new build once it's safe.
const MaintenanceGate = ({ children }) => {
  const user = useSelector(selectCurrentUser);
  const isAdmin = user?.role === "admin";
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;

  const [maintenance, setMaintenance] = useState(false);
  const wasMaintenance = useRef(false);
  // Guards against an infinite reload loop: the poll right after a forced
  // reload (from either branch below) lands on a FRESH mount, where
  // wasMaintenance would start back at false again — without this guard,
  // loading straight into an active maintenance window (which is exactly
  // what just happened) would immediately look like a brand-new "deploy
  // just started" transition and reload again, forever. Only transitions
  // detected from the SECOND poll onward (i.e. actually observed changing
  // during this page's lifetime) should trigger a reload.
  const isFirstPoll = useRef(true);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${STATUS_URL}?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) throw new Error("not found");
        const data = await res.json();
        if (cancelled) return;

        const isMaintenance = !!data.maintenance;

        if (isFirstPoll.current) {
          isFirstPoll.current = false;
          wasMaintenance.current = isMaintenance;
          setMaintenance(isMaintenance);
          return;
        }

        // Deploy just finished — reload to fetch the new build instead of
        // continuing to run the old one indefinitely. Applies to admins too.
        if (wasMaintenance.current && !isMaintenance) {
          window.location.reload();
          return;
        }
        // Deploy just started — reload non-admins straight into the
        // maintenance page, even if they're mid-interaction. Admins are
        // left alone (just the banner below) so a deploy doesn't interrupt
        // their work.
        if (!wasMaintenance.current && isMaintenance && !isAdminRef.current) {
          window.location.reload();
          return;
        }

        wasMaintenance.current = isMaintenance;
        setMaintenance(isMaintenance);
      } catch {
        // No status file (local dev, or it hasn't been created yet) —
        // treat as "not in maintenance", same as a normal healthy site.
        if (!cancelled) {
          isFirstPoll.current = false;
          wasMaintenance.current = false;
          setMaintenance(false);
        }
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (maintenance && !isAdmin) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          textAlign: "center",
          px: 3,
          bgcolor: "#0E1420",
          color: "white",
        }}
      >
        <Build sx={{ fontSize: 48, opacity: 0.85 }} />
        <Typography variant="h5" fontWeight={700}>
          We'll be right back
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.75, maxWidth: 420 }}>
          Waypoint Imports is deploying an update. This usually takes just a couple of minutes —
          this page will automatically refresh once it's done.
        </Typography>
        <CircularProgress size={22} sx={{ color: "white", opacity: 0.6, mt: 1 }} />
      </Box>
    );
  }

  return (
    <>
      {maintenance && isAdmin && (
        <Box
          sx={{
            bgcolor: "warning.main",
            color: "warning.contrastText",
            textAlign: "center",
            py: 0.5,
            fontSize: 13,
            fontWeight: 600,
            position: "sticky",
            top: 0,
            zIndex: 2000,
          }}
        >
          A deploy is in progress — customers are seeing the maintenance page right now
        </Box>
      )}
      {children}
    </>
  );
};

export default MaintenanceGate;
